import { NumericRange } from 'rlv-common';
import { shouldShowPublicView } from '../../Helper';
import InfoText from '../../components/InfoText/infoText';
import Price from '../../components/Price/Price';
import { getAgentPhone, getIsTiedAgent } from '../../helpers/modeConfig';
import { TrackingElement } from '../../tracking/tracking.types';
import { AemFragmentCatalog } from '../../types/AemFragmentCatalog';
import { TemplateLayout } from '../../types/FormTemplate.types';
import { ConstantNeedBlockData, DecreasingNeedData } from './Needs.types';

export enum ComponentFieldNames {
  SumInsured = 'sumInsured',
  PeriodInYears = 'periodInYears',
  InsuranceEndSum = 'insuranceEndSum',
}

export enum SliderFormValues {
  DefaultPeriodInYears = 20,
  DefaultInsuranceEndSum = 5000,
  DefaultInsuranceSumInterval = 5000,
}

export const getPeriodInYears = (
  storedDuration: number | undefined,
  maxDuration: number,
): number => {
  if (storedDuration) {
    return storedDuration;
  }
  if (maxDuration < SliderFormValues.DefaultPeriodInYears) {
    return maxDuration;
  }

  return SliderFormValues.DefaultPeriodInYears;
};

export const DECREASING_SLIDER_FORM_TEMPLATES_LAYOUT: {
  [K in keyof Required<
    Pick<DecreasingNeedData, 'sumInsured' | 'periodInYears' | 'insuranceEndSum'>
  >]: TemplateLayout<NumericRange>;
} = {
  sumInsured: {
    title: 'Legen Sie Ihre Versicherungssumme für das erste Jahr fest',
    subline: '',
    tooltip: (
      <div>
        <b>So wählen Sie Ihre linear fallende Versicherungssumme:</b>
        <br />
        <br />
        Die gewählte Summe sollte für das erste Jahr mindestens so hoch sein, dass Ihre
        Hinterbliebenen weiterhin z. B. Kredit- und Darlehensraten damit bezahlen können, falls Sie
        sterben. Wenn Sie sichergehen möchten, dass ausreichend Geld vorhanden ist, runden Sie die
        Startsumme auf.
        <br />
        <br />
        Im Folgenden können Sie die Laufzeit und die Summe für das letzte Jahr festlegen.
        <br />
        <br />
        <b>Gut zu wissen: </b>ERGO bietet Ihnen bei Bedarf auch individuelle und sehr flexible
        Lösungen für den Verlauf an. Lassen Sie sich dazu persönlich beraten – beim{' '}
        <b>gebührenfreien telefonischen Beratungsservice</b> (7 bis 24 Uhr) oder Ihrem{' '}
        <b>
          <a target="_blank" href="https://www.ergo.de/de/Service/Vermittlersuche">
            ERGO Berater vor Ort.
          </a>
        </b>
      </div>
    ),
    tooltipTracking: 'InfoIcon_FallendVersSumEJ' as TrackingElement,
    footerInfoSection: sumInsured => (
      <InfoText
        textBeforeLink={
          <span>
            Sie möchten mehr als{' '}
            {<Price amount={String(sumInsured?.max)} unstyled hideFractionDigits />} absichern? Dann
            wenden Sie sich an Ihren
          </span>
        }
        linkUrl={
          shouldShowPublicView(getIsTiedAgent())
            ? ''
            : 'https://www.ergo.de/de/Service/Vermittlersuche'
        }
        linkText={shouldShowPublicView(getIsTiedAgent()) ? 'ERGO Berater:' : 'ERGO Berater'}
        textAfterLink={getAgentPhone() || ''}
      />
    ),
    virtualAssistantFragment: AemFragmentCatalog.STEP4VA1ALT,
  },
  periodInYears: {
    title: 'Für wie lange möchten Sie Ihr Darlehen oder Ihren Kredit absichern?',
    subline: '',
    footerInfoSection: () => (
      <InfoText textBeforeLink="Sie haben Fragen oder möchten sich beraten lassen? Dann rufen Sie einfach an." />
    ),
    virtualAssistantFragment: AemFragmentCatalog.STEP5VA1ALT,
  },

  insuranceEndSum: {
    title: 'Legen Sie Ihre Versicherungssumme für das letzte Jahr fest',
    subline: '',
    footerInfoSection: () => (
      <InfoText textBeforeLink="Sie haben Fragen oder möchten sich beraten lassen? Dann rufen Sie einfach an." />
    ),
    virtualAssistantFragment: AemFragmentCatalog.STEP6VAALT,
  },
};

export const CONSTANT_SLIDER_FORM_TEMPLATES_LAYOUT: {
  [K in keyof Required<
    Pick<ConstantNeedBlockData, 'sumInsured' | 'periodInYears'>
  >]: TemplateLayout<NumericRange>;
} = {
  sumInsured: {
    title: 'Legen Sie Ihre Versicherungssumme fest',
    tooltip: (
      <div>
        <b>So wählen Sie Ihre Versicherungssumme:</b>
        <br />
        Die gewählte Summe sollte so hoch sein, dass Ihre Familie oder Ihr Partner im Todesfall den
        Lebensstandard ohne Ihr Einkommen oder Ihre Care-Arbeit gut halten kann. Wenn Sie Kinder
        haben am besten so lange, bis sie finanziell unabhängig sind.
        <br />
        <br />
        Bei ERGO können Sie Ihre Versicherungssumme nachträglich erhöhen, wenn sich Ihr Leben
        ändert.
        <br />
        <br />
        <b>Gut zu wissen: </b>Es handelt sich um eine reine Risikoabsicherung für den Todesfall, d.
        h. es gibt keine Auszahlung am Ende der Laufzeit.
      </div>
    ),
    tooltipTracking: 'InfoIcon_KonstVersSum' as TrackingElement,
    subline: '',
    virtualAssistantFragment: AemFragmentCatalog.STEP4VA1,
    footerInfoSection: sumInsured => (
      <InfoText
        textBeforeLink={
          <span>
            Sie möchten mehr als{' '}
            {<Price amount={String(sumInsured?.max)} unstyled hideFractionDigits />} absichern? Dann
            wenden Sie sich an Ihren
          </span>
        }
        linkUrl={
          shouldShowPublicView(getIsTiedAgent())
            ? ''
            : 'https://www.ergo.de/de/Service/Vermittlersuche'
        }
        linkText={shouldShowPublicView(getIsTiedAgent()) ? 'ERGO Berater:' : 'ERGO Berater'}
        textAfterLink={getAgentPhone() || ''}
      />
    ),
  },
  periodInYears: {
    title: 'Für wie lange möchten Sie Ihre Hinterbliebenen absichern?',
    subline: '',
    virtualAssistantFragment: AemFragmentCatalog.STEP5VA1,
  },
};
